import { PayloadAction, createSlice } from '@reduxjs/toolkit';
import type { RootState } from '../store';

export type ServiceStateReason =
  | 'service_unavailable'
  | 'maintenance'
  | 'network';

export const NETWORK_FAILURE_WINDOW_MS = 30_000;
export const NETWORK_FAILURE_THRESHOLD = 3;
export const PROBE_MIN_SECONDS = 30;
export const PROBE_MAX_SECONDS = 60;

interface NetworkFailure {
  path: string;
  at: number;
}

interface ServiceState {
  status: 'ok' | 'unavailable';
  reason: ServiceStateReason | null;
  retryAfterSeconds: number | null;
  since: number | null;
  nextProbeAt: number | null;
  recentNetworkFailures: NetworkFailure[];
}

const initialState: ServiceState = {
  status: 'ok',
  reason: null,
  retryAfterSeconds: null,
  since: null,
  nextProbeAt: null,
  recentNetworkFailures: [],
};

// Retry-After can be hours during maintenance; the app still re-checks within
// a minute so recovery is noticed, and never faster than 30 s.
const probeDelayMs = (retryAfterSeconds: number | null) =>
  Math.min(
    Math.max(retryAfterSeconds ?? PROBE_MIN_SECONDS, PROBE_MIN_SECONDS),
    PROBE_MAX_SECONDS
  ) * 1000;

const markUnavailable = (
  state: ServiceState,
  reason: ServiceStateReason,
  retryAfterSeconds: number | null,
  at: number
) => {
  state.since = state.status === 'unavailable' ? state.since : at;
  state.status = 'unavailable';
  state.reason = reason;
  state.retryAfterSeconds = retryAfterSeconds;
  state.nextProbeAt = at + probeDelayMs(retryAfterSeconds);
  state.recentNetworkFailures = [];
};

const ServiceStateSlice = createSlice({
  name: 'serviceState',
  initialState,
  reducers: {
    serviceUnavailable: (
      state,
      action: PayloadAction<{
        reason: ServiceStateReason;
        retryAfterSeconds: number | null;
        at: number;
      }>
    ) => {
      const { reason, retryAfterSeconds, at } = action.payload;
      markUnavailable(state, reason, retryAfterSeconds, at);
    },
    networkFailureObserved: (
      state,
      action: PayloadAction<{ path: string; at: number }>
    ) => {
      const { path, at } = action.payload;
      const recent = state.recentNetworkFailures.filter(
        failure =>
          failure.path !== path && at - failure.at <= NETWORK_FAILURE_WINDOW_MS
      );
      recent.push({ path, at });
      if (recent.length >= NETWORK_FAILURE_THRESHOLD) {
        if (state.status === 'ok') markUnavailable(state, 'network', null, at);
        state.recentNetworkFailures = [];
        return;
      }
      state.recentNetworkFailures = recent;
    },
    probeClaimed: (state, action: PayloadAction<{ at: number }>) => {
      state.nextProbeAt =
        action.payload.at + probeDelayMs(state.retryAfterSeconds);
    },
    answered: (state, action: PayloadAction<{ startedAt: number }>) => {
      if (
        state.status === 'unavailable' &&
        action.payload.startedAt >= (state.since ?? 0)
      ) {
        return initialState;
      }
      state.recentNetworkFailures = [];
    },
  },
});

export const selectServiceState = (state: RootState) => state.serviceState;
export const selectIsServiceUnavailable = (state: RootState) =>
  state.serviceState.status === 'unavailable';

export const {
  serviceUnavailable,
  networkFailureObserved,
  probeClaimed,
  answered,
} = ServiceStateSlice.actions;

export default ServiceStateSlice.reducer;
